<template>
  <section>
    <div class="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 class="text-xl font-bold text-zinc-950">Webinar analytics</h2>
        <p class="mt-1 text-sm text-zinc-500">Registration conversion and member watch-time across your Webinar events.</p>
      </div>
      <select v-model="range" class="h-10 rounded-xl border border-zinc-300 bg-white px-3 text-sm font-semibold text-zinc-800">
        <option value="30d">Last 30 days</option><option value="90d">Last 90 days</option><option value="365d">Last year</option><option value="lifetime">Lifetime</option>
      </select>
    </div>

    <div v-if="loading" class="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
      <div v-for="card in 5" :key="card" class="h-28 animate-pulse rounded-2xl bg-zinc-100" />
    </div>
    <div v-else-if="error" class="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm font-medium text-red-700">{{ error }}</div>
    <template v-else>
      <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <article v-for="card in cards" :key="card.label" class="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
          <p class="text-xs font-bold uppercase tracking-wide text-zinc-500">{{ card.label }}</p>
          <p class="mt-2 text-2xl font-bold text-zinc-950">{{ card.value }}</p>
        </article>
      </div>
      <div class="mt-5 overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
        <div class="border-b border-zinc-200 px-5 py-4"><h3 class="text-sm font-bold text-zinc-900">Event performance</h3></div>
        <div v-if="events.length" class="overflow-x-auto">
          <table class="min-w-full divide-y divide-zinc-200 text-sm">
            <thead class="bg-zinc-50 text-left text-xs font-bold uppercase tracking-wide text-zinc-500"><tr><th class="px-5 py-3">Event</th><th class="px-4 py-3">Registrants</th><th class="px-4 py-3">Show-ups</th><th class="px-4 py-3">Conversion</th><th class="px-4 py-3">Avg. watch</th></tr></thead>
            <tbody class="divide-y divide-zinc-100">
              <tr v-for="event in events" :key="event.eventId" class="hover:bg-zinc-50/70">
                <td class="px-5 py-4"><p class="max-w-md truncate font-semibold text-zinc-900">{{ event.title }}</p><p class="mt-1 text-xs capitalize text-zinc-500">{{ event.status }}</p></td>
                <td class="px-4 py-4 font-medium text-zinc-700">{{ event.registrationCount }}</td><td class="px-4 py-4 font-medium text-zinc-700">{{ event.showUpCount }}</td><td class="px-4 py-4 font-semibold text-zinc-900">{{ event.conversionRate }}%</td><td class="px-4 py-4 text-zinc-600">{{ duration(event.averageWatchSeconds) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="px-6 py-14 text-center text-sm text-zinc-500">No Webinar activity in this range yet.</div>
      </div>
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
const apiProxyUrl = String(process.env.VUE_APP_API_PROXY_URL || '').replace(/\/$/, '');
const range = ref('30d'); const loading = ref(true); const error = ref(''); const payload = ref({ summary: {}, events: [] });
const summary = computed(() => payload.value?.summary || {}); const events = computed(() => Array.isArray(payload.value?.events) ? payload.value.events : []);
const cards = computed(() => [
  { label: 'Events', value: Number(summary.value.eventCount || 0).toLocaleString() },
  { label: 'Registrants', value: Number(summary.value.registrationCount || 0).toLocaleString() },
  { label: 'Show-ups', value: Number(summary.value.showUpCount || 0).toLocaleString() },
  { label: 'Show-up rate', value: `${Number(summary.value.conversionRate || 0)}%` },
  { label: 'Avg. watch time', value: duration(summary.value.averageWatchSeconds) },
]);
function duration(seconds) { const total = Math.max(0, Number(seconds || 0)); const hours = Math.floor(total / 3600); const minutes = Math.floor((total % 3600) / 60); return hours ? `${hours}h ${minutes}m` : `${minutes}m`; }
async function load() { loading.value = true; error.value = ''; try { const response = await fetch(`${apiProxyUrl}/api/creator-webinars/analytics?range=${encodeURIComponent(range.value)}`, { credentials: 'include', headers: { Accept: 'application/json' } }); const data = await response.json().catch(() => ({})); if (!response.ok) throw new Error(data?.message || 'Unable to load Webinar analytics'); payload.value = data; } catch (requestError) { error.value = String(requestError?.message || 'Unable to load Webinar analytics'); } finally { loading.value = false; } }
watch(range, load); onMounted(load);
</script>
